// Base Libs
import React, { useState, InputEvent, useEffect, useRef } from "react";
import { shallowEqual } from "react-redux";
import {
    GuxButton,
    GuxFormFieldTextLike,
    GuxIcon,
    GuxFormFieldNumber,
    GuxFormFieldTextarea,
} from "genesys-spark-components-react";
import {
    AssessmentFormQuestionTypeEnum,
    EvaluationQuestionTypeEnum,
    EvaluationQuestionAutomatedScoringFocusEnum,
    SurveyQuestionTypeEnum,
} from "@genesys/cloud-frontend/models";

// Styles
import "./QuestionEditor.less";

// General common
import { useAppDispatch, useAppSelector } from "app/hooks";
import { RootState } from "app/store";
import { DefaultLimits } from "constants/DefaultLimits";
import FormType from "enums/FormType";
import FeatureToggle from "enums/FeatureToggle";
import AutomatedScoringType from "enums/AutomatedScoringType";

// Feature common
import { useStrings } from "features/i18n/useStrings";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import {
    invalidIntegerInputCharacters,
    selectIsQuestionHavingValidMaxResponseChar,
    selectIsFollowUpExplanationTooLengthy,
    selectIsQuestionNamePresent,
    selectIsQuestionNameTooLengthy,
    selectIsQuestionHelpTextValid,
} from "features/qualityForm/qualityFormValidations";

// Feature slices
import {
    qualityFormActions,
    selectEligibleQuestionsForVisibilityConditionsForQuestion,
    selectEvaluationFormDefaultAnswers,
    selectNpsPrompt,
    selectQuestionGroupIdContainingQuestion,
    toggleConditionalDisplay,
} from "features/qualityForm/qualityFormSlice";
import {
    selectAutomatedScoringFocus,
    selectFormType,
    selectIsAutomatedEvaluationForm,
    selectQuestion,
    selectSelectedAutomatedScoringType,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";

// Form Editor common
import { Property, PropertySection } from "../common/PropertySection";
import { disableInputScroll } from "../common/DisableInputScroll";
import { autoFocusRef } from "../common/inputUtils";
import { QuestionVisibilityConditions } from "../common/VisibilityConditions/VisibilityConditionContainer";

// Locales
import strings from "./Question.strings";
import answerPropsStrings from "./AnswerPropertiesSection.strings";

// Locals
import { SaveQuestionAsTemplateButton } from "./SaveQuestionTemplate";
import AutomatedScoringSection from "./AutomatedScoringSection";
import EvaluationFocusSection from "./EvaluationFocusSection";
import AnswerPropertiesSection from "./AnswerPropertiesSection";
import AnswerOptionsSection from "./AnswerOptionsSection";
import MultipleSelectAnswerOptionsSection from "./MultipleSelectAnswerOptionsSection";
import ScoringOptionsSection from "./ScoringOptionsSection";

export default function QuestionEditor({
    questionId,
    onDone,
    onDelete,
    shouldAutoFocusQuestionText,
    onQuestionTextAutoFocused,
}: {
    questionId: string;
    onDone: React.MouseEventHandler<HTMLGuxButtonElement>;
    onDelete: () => void;
    shouldAutoFocusQuestionText?: boolean;
    onQuestionTextAutoFocused?: () => void;
}) {
    const hasDefinedExplanationPrompt = useAppSelector((state) => {
        return selectNpsPrompt(state, questionId) !== undefined;
    });

    const [npsPrompt, setNpsPrompt] = useState(hasDefinedExplanationPrompt);
    const {
        text,
        helpText,
        questionType,
        naEnabled,
        isConditionallyVisible,
        maxResponseCharacters,
    } = useAppSelector((state) => {
        const question = selectQuestion(state, questionId);
        return {
            text: question?.text,
            helpText: question?.helpText,
            questionType: question?.type,
            naEnabled: question?.naEnabled,
            isConditionallyVisible: question?.visibilityCondition !== undefined,
            maxResponseCharacters: question?.maxResponseCharacters,
        };
    }, shallowEqual);

    const formType = useAppSelector((state) => selectFormType(state));

    const allowScoringOptions =
        formType !== FormType.SURVEY &&
        (questionType === EvaluationQuestionTypeEnum.MultipleChoiceQuestion ||
            questionType ===
                AssessmentFormQuestionTypeEnum.MultipleChoiceQuestion);

    // Import all FTs
    const isAiScoringFTEnabled = useAppSelector((state) =>
        selectFeatureToggle(state, FeatureToggle.AI_SCORING)
    );
    const isVoiceSurveyFeatureEnabled = useAppSelector((state) =>
        selectFeatureToggle(state, FeatureToggle.VOICE_SURVEY)
    );
    const isMultipleSelectFTEnabled = useAppSelector((state) =>
        selectFeatureToggle(state, FeatureToggle.MULTIPLE_SELECT)
    );
    const isWem1511EvaluationFocusEnabled = useAppSelector((state) =>
        selectFeatureToggle(state, FeatureToggle.WEM_1511_EVALUATION_FOCUS)
    );

    // Feature Toggle check required?
    const automatedScoringOption = useAppSelector((state) =>
        selectSelectedAutomatedScoringType(state, questionId)
    );

    const isAutomatedEvaluationForm = useAppSelector(
        selectIsAutomatedEvaluationForm
    );

    const questionGroupId = useAppSelector(
        (state) =>
            selectQuestionGroupIdContainingQuestion(questionId)(
                state.qualityForm
            ) ?? ""
    );

    const isDefaultAnswersToNASelected =
        useAppSelector(
            (state) =>
                selectEvaluationFormDefaultAnswers(state, questionGroupId)
                    ?.notApplicable
        ) ?? false;

    // Automated Scoring
    const dispatch = useAppDispatch();
    const [selectedAutomatedScoringOption, setSelectedAutomatedScoringOption] =
        useState(automatedScoringOption);
    const saveNewAutomatedScoringSelection = (
        newSelectedOption: AutomatedScoringType
    ) => {
        setSelectedAutomatedScoringOption(newSelectedOption);
        dispatch(
            qualityFormActions.setAutomatedScoringOption({
                questionId,
                automatedScoringOption: newSelectedOption,
            })
        );
    };

    const automatedScoringFocus = useAppSelector((state) =>
        selectAutomatedScoringFocus(state, questionId)
    );
    const saveAutomatedScoringFocus = (
        focus: EvaluationQuestionAutomatedScoringFocusEnum
    ) => {
        dispatch(
            qualityFormActions.setAutomatedScoringFocus({
                questionId,
                automatedScoringFocus: focus,
            })
        );
    };

    const allowVisibilityConditions = useAppSelector(
        (state) =>
            (selectEligibleQuestionsForVisibilityConditionsForQuestion(
                state,
                questionId,
                true
            )?.length ?? 0) > 0
    );

    const hasTemplateFunctionality = useAppSelector(
        (state) => selectFormType(state) === FormType.EVALUATION
    );

    const {
        explanationPrompt,
        hasEmptyExplanationPrompt,
        hasTooLengthyExplanationPrompt,
    } = useAppSelector((state) => {
        const explanationPrompt = selectNpsPrompt(state, questionId);
        return {
            explanationPrompt: explanationPrompt,
            hasEmptyExplanationPrompt: explanationPrompt?.trim() === "",
            hasTooLengthyExplanationPrompt:
                selectIsFollowUpExplanationTooLengthy(state, questionId),
        };
    }, shallowEqual);
    const isNamePresent = useAppSelector((state: RootState) =>
        selectIsQuestionNamePresent(state, questionId)
    );
    const isNameTooLong = useAppSelector((state: RootState) =>
        selectIsQuestionNameTooLengthy(state, questionId)
    );
    const isMaxResponseCharSizeInvalid = useAppSelector(
        (state) =>
            !selectIsQuestionHavingValidMaxResponseChar(state, questionId)
    );
    const isHelpTextValid = useAppSelector((state) =>
        selectIsQuestionHelpTextValid(state, questionId)
    );
    const t = useStrings(strings);
    const answerPropsT = useStrings(answerPropsStrings);

    const explationPromptErrorMessage = hasEmptyExplanationPrompt
        ? t.labels.helpText.nps.fieldIsEmptyError
        : hasTooLengthyExplanationPrompt
        ? t.labels.helpText.nps.explanationIsTooLengthy({
              maxExplanationPromptLength:
                  DefaultLimits.maxExplanationPromptLength,
          })
        : undefined;

    const questionNameError = !isNamePresent
        ? t.errors.requiredField
        : isNameTooLong
        ? t.errors.questionNameTooLong({
              maxQuestionNameLength: DefaultLimits.maxQuestionNameLength,
          })
        : undefined;

    const numberInputRef = useRef<HTMLInputElement>(null);
    useEffect(() => disableInputScroll(numberInputRef));
    const questionTextRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (shouldAutoFocusQuestionText) {
            return autoFocusRef(questionTextRef, onQuestionTextAutoFocused);
        }
    }, [shouldAutoFocusQuestionText, onQuestionTextAutoFocused]);

    let showAutomatedAiScoringSection = false;
    if (isAiScoringFTEnabled) {
        if (isMultipleSelectFTEnabled) {
            showAutomatedAiScoringSection =
                formType === FormType.EVALUATION &&
                questionType !==
                    EvaluationQuestionTypeEnum.MultipleSelectQuestion;
        } else {
            showAutomatedAiScoringSection = formType === FormType.EVALUATION;
        }
    }

    let showAnswerPropertiesSection = false;
    if (isMultipleSelectFTEnabled) {
        showAnswerPropertiesSection =
            questionType !== SurveyQuestionTypeEnum.ReadOnlyTextBlockQuestion &&
            questionType !== SurveyQuestionTypeEnum.NpsQuestion &&
            questionType !== EvaluationQuestionTypeEnum.MultipleSelectQuestion;
    } else {
        showAnswerPropertiesSection =
            questionType !== SurveyQuestionTypeEnum.ReadOnlyTextBlockQuestion &&
            questionType !== SurveyQuestionTypeEnum.NpsQuestion;
    }

    showAnswerPropertiesSection =
        showAnswerPropertiesSection &&
        (!isAutomatedEvaluationForm || isDefaultAnswersToNASelected);

    const showAnswerOptions =
        questionType === EvaluationQuestionTypeEnum.MultipleChoiceQuestion ||
        questionType === SurveyQuestionTypeEnum.MultipleChoiceQuestion ||
        questionType === AssessmentFormQuestionTypeEnum.MultipleChoiceQuestion;

    // When it comes to edit mode, if FT is off, it should not be shown anyways.
    // This is the assumption.
    const showMultipleSelectAnswerOptions =
        isMultipleSelectFTEnabled &&
        questionType === EvaluationQuestionTypeEnum.MultipleSelectQuestion;

    const showEvaluationFocusSection =
        showAutomatedAiScoringSection &&
        isWem1511EvaluationFocusEnabled &&
        (selectedAutomatedScoringOption === AutomatedScoringType.AI_SCORING ||
            selectedAutomatedScoringOption ===
                AutomatedScoringType.EVALUATION_ASSISTANCE);

    // Help Text Expansion
    const canExpandHelpText =
        formType === FormType.EVALUATION || formType === FormType.SURVEY;

    const handleHelpTextChange = (
        e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        dispatch(
            qualityFormActions.changeQuestionHelpText({
                text: e.currentTarget.value,
                questionId: questionId,
            })
        );
    };

    const HelpTextWrapper = canExpandHelpText
        ? GuxFormFieldTextarea
        : GuxFormFieldTextLike;
    // NOTE: for Evaluation Form, the validation won't appear in UI because there is no implementation for this limit in the backend
    const helpTextError = !isHelpTextValid && (
        <span slot="error" role="alert" data-testid="help-text-error">
            {t.errors.helpTextTooLong({
                maxHelpTextLength: DefaultLimits.maxHelpTextLength,
            })}
        </span>
    );
    const helpTextInputProps = {
        slot: "input" as const,
        name: "helpText",
        value: helpText,
        placeholder: t.labels.helpText.content,
        onInput: handleHelpTextChange,
    };

    return (
        <div className="QuestionEditor">
            <GuxFormFieldTextarea
                label-position="above"
                className="question-name-input"
                resize="auto"
            >
                <label slot="label">{t.labels.question}</label>
                <textarea
                    ref={questionTextRef}
                    slot="input"
                    name="textarea"
                    required
                    value={text}
                    onInput={({ currentTarget }) => {
                        dispatch(
                            qualityFormActions.changeQuestionName({
                                text: currentTarget.value,
                                questionId: questionId,
                            })
                        );
                    }}
                ></textarea>
                {questionNameError && (
                    <span slot="error" role="alert">
                        {questionNameError}
                    </span>
                )}
            </GuxFormFieldTextarea>

            <HelpTextWrapper
                label-position="above"
                className="help-text-input"
                {...(canExpandHelpText
                    ? { resize: "auto" }
                    : { clearable: true })}
            >
                <label slot="label">{t.labels.helpText.title}</label>
                {canExpandHelpText ? (
                    <textarea {...helpTextInputProps}></textarea>
                ) : (
                    <input {...helpTextInputProps} type="text" />
                )}
                {helpTextError}
            </HelpTextWrapper>

            {showAutomatedAiScoringSection && (
                <AutomatedScoringSection
                    key={questionId}
                    questionId={questionId}
                    automatedScoringOption={selectedAutomatedScoringOption}
                    onSelected={saveNewAutomatedScoringSelection}
                />
            )}

            {showEvaluationFocusSection && (
                <EvaluationFocusSection
                    key={`${questionId}-focus`}
                    questionId={questionId}
                    automatedScoringFocus={automatedScoringFocus}
                    onChange={saveAutomatedScoringFocus}
                />
            )}

            {showAnswerOptions && (
                <AnswerOptionsSection questionId={questionId} />
            )}

            {showMultipleSelectAnswerOptions && (
                <MultipleSelectAnswerOptionsSection questionId={questionId} />
            )}

            {showAnswerPropertiesSection && (
                <AnswerPropertiesSection
                    questionId={questionId}
                    formType={formType}
                    numberInputRef={numberInputRef}
                ></AnswerPropertiesSection>
            )}

            {questionType === SurveyQuestionTypeEnum.NpsQuestion && (
                <PropertySection title={answerPropsT.labels.nps.title}>
                    <Property
                        name="followUpRequest"
                        checked={npsPrompt}
                        onChange={() => {
                            if (!npsPrompt) {
                                dispatch(
                                    qualityFormActions.setExplanationPrompt({
                                        questionId: questionId,
                                        explanationPrompt: "",
                                    })
                                );
                            } else {
                                dispatch(
                                    qualityFormActions.removeExplanationPrompt({
                                        questionId: questionId,
                                    })
                                );
                            }
                            setNpsPrompt(!npsPrompt);
                        }}
                        label={answerPropsT.labels.nps.requestFollowUp}
                    />
                    {npsPrompt && (
                        <>
                            <Property
                                name="optionalCheck"
                                checked={naEnabled}
                                onChange={() => {
                                    dispatch(
                                        qualityFormActions.setNAEnabledQuestion(
                                            {
                                                questionId: questionId,
                                                value: !naEnabled,
                                            }
                                        )
                                    );
                                }}
                                label={answerPropsT.labels.nps.optionalCheck}
                            />
                            <GuxFormFieldNumber
                                className="nps-max-characters"
                                labelPosition="above"
                            >
                                <input
                                    slot="input"
                                    name="number"
                                    type="number"
                                    ref={numberInputRef}
                                    step="1"
                                    min={DefaultLimits.minResponseCharacters}
                                    max={DefaultLimits.maxResponseCharacters}
                                    value={maxResponseCharacters}
                                    onBeforeInput={(
                                        event: InputEvent<HTMLInputElement>
                                    ) => {
                                        if (
                                            invalidIntegerInputCharacters.includes(
                                                event.data
                                            )
                                        ) {
                                            event.preventDefault();
                                        }
                                    }}
                                    onChange={({ currentTarget }) => {
                                        dispatch(
                                            qualityFormActions.setMaxResponseCharacters(
                                                {
                                                    questionId,
                                                    maxResponseCharacters:
                                                        currentTarget.value,
                                                }
                                            )
                                        );
                                    }}
                                />
                                <label slot="label">
                                    {
                                        answerPropsT.labels.nps
                                            .maximumResponseCharacters
                                    }
                                </label>
                                {isMaxResponseCharSizeInvalid && (
                                    <span slot="error" role="alert">
                                        {answerPropsT.errors.maxResponseCharsInvalid(
                                            {
                                                maxResponseCharacters:
                                                    DefaultLimits.maxResponseCharacters,
                                            }
                                        )}
                                    </span>
                                )}
                            </GuxFormFieldNumber>
                            <GuxFormFieldTextarea
                                label-position="above"
                                className="input-field"
                                resize="auto"
                            >
                                <textarea
                                    slot="input"
                                    name="textarea"
                                    required
                                    value={explanationPrompt}
                                    onInput={(event) => {
                                        dispatch(
                                            qualityFormActions.setExplanationPrompt(
                                                {
                                                    questionId: questionId,
                                                    explanationPrompt: (
                                                        event.target as HTMLTextAreaElement
                                                    ).value,
                                                }
                                            )
                                        );
                                    }}
                                ></textarea>
                                <label slot="label">
                                    {t.labels.helpText.nps.followUpExplanation}
                                </label>
                                {explationPromptErrorMessage && (
                                    <span slot="error" role="alert">
                                        {explationPromptErrorMessage}
                                    </span>
                                )}
                            </GuxFormFieldTextarea>
                        </>
                    )}
                </PropertySection>
            )}
            {allowVisibilityConditions && (
                <PropertySection title={t.labels.logic.title}>
                    <Property
                        name="displayConditionally"
                        checked={isConditionallyVisible}
                        onChange={() => {
                            dispatch(
                                toggleConditionalDisplay(
                                    questionId,
                                    "question",
                                    isMultipleSelectFTEnabled
                                )
                            );
                        }}
                        label={
                            isVoiceSurveyFeatureEnabled
                                ? formType === FormType.SURVEY
                                    ? t.labels.logic.includeConditionally
                                    : t.labels.logic.displayConditionally
                                : t.labels.logic.displayConditionally
                        }
                    />
                    {isConditionallyVisible && (
                        <QuestionVisibilityConditions questionId={questionId} />
                    )}
                </PropertySection>
            )}
            {allowScoringOptions && (
                <ScoringOptionsSection questionId={questionId} />
            )}
            <div className="action-buttons">
                <div className="spaced-buttons">
                    <GuxButton
                        onClick={() => onDelete()}
                        data-testid="delete-button"
                    >
                        <GuxIcon icon-name="delete" decorative />
                        <span>{t.labels.delete}</span>
                    </GuxButton>
                    {hasTemplateFunctionality && (
                        <SaveQuestionAsTemplateButton questionId={questionId} />
                    )}
                </div>

                <GuxButton accent="primary" onClick={onDone}>
                    {t.labels.done}
                </GuxButton>
            </div>
        </div>
    );
}
